/**
 * Mobile Phase 2, chunk 2: the Tasks tab and task detail. Keys match the web's (['tasks-area', ws, filter],
 * qk.task, qk.project, ['task-comments', id]) so the desktop panel and the phone screens share one cache.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { SubtaskItem, TaskCreateFields, TaskDetail, TaskListFilter, TaskListItem, TaskSummary, UpdateTaskInput } from '@tradeoutlet/shared';
import { dataApi } from './client';
import { qk } from './keys';

type Ws = string | null | undefined;
/** The same filter object the web Tasks area builds for "For me · open", so the key matches. */
export const MY_OPEN_TASKS: TaskListFilter = { project: 'all', scope: 'mine', status: 'open' };
/** t196u: the phone's Done chip; the same filter as the web's "For me · done". */
export const MY_DONE_TASKS: TaskListFilter = { project: 'all', scope: 'mine', status: 'done' };
export const taskKeys = {
  mine: (ws: Ws) => ['tasks-area', ws ?? '', MY_OPEN_TASKS] as const,
  mineDone: (ws: Ws) => ['tasks-area', ws ?? '', MY_DONE_TASKS] as const,
  comments: (id: string) => ['task-comments', id] as const,
};
/** After any task change: lists, Today's Needs me, Home's task stats, the panel. */
export function invalidateTasks(qc: QueryClient, ws: Ws, id?: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['tasks-area'] }), qc.invalidateQueries({ queryKey: ['my-tasks'] }),
    qc.invalidateQueries({ queryKey: ['wf', ws, 'me-today'] }), qc.invalidateQueries({ queryKey: ['nav-badges', ws ?? ''] }),
    ...(id ? [qc.invalidateQueries({ queryKey: qk.task(id) })] : []),
  ]);
}

/**
 * t197u: a changed task, written into every Tasks-area list straight away (desktop and phone share the keys): updated in
 * place, and dropped from a list whose Open / Done filter it no longer matches. A refetch then adds it where it now belongs.
 */
export function patchTaskLists(qc: QueryClient, task: Partial<TaskListItem> & { id: string }) {
  for (const [key, data] of qc.getQueriesData<{ tasks: TaskListItem[] }>({ queryKey: ['tasks-area'] })) {
    if (!data?.tasks?.some(t => t.id === task.id)) continue;
    const filter = (key[2] ?? {}) as TaskListFilter;
    const tasks = data.tasks.flatMap(t => {
      if (t.id !== task.id) return [t];
      const next = { ...t, ...task } as TaskListItem;
      if (filter.status === 'open' && next.completed_at) return [];
      if (filter.status === 'done' && !next.completed_at) return [];
      return [next];
    });
    qc.setQueryData(key, { ...data, tasks });
  }
}

export function useMyOpenTasks(ws: Ws) {
  return useQuery({ queryKey: taskKeys.mine(ws), queryFn: () => dataApi().workspaceTasks(ws!, MY_OPEN_TASKS), enabled: Boolean(ws), select: d => d.tasks });
}

export function useMyDoneTasks(ws: Ws, enabled = true) {
  return useQuery({ queryKey: taskKeys.mineDone(ws), queryFn: () => dataApi().workspaceTasks(ws!, MY_DONE_TASKS), enabled: Boolean(ws) && enabled, select: d => d.tasks });
}

type Versioned = Pick<TaskSummary, 'id' | 'version'>;
/**
 * Complete or reopen a task (`done` shortcut). Completing hides it from My tasks at once; on failure it comes back.
 * Returns the updated task, whose version the Undo needs.
 */
export function useSetTaskDone(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { task: Versioned; done: boolean }) => dataApi().updateTask(a.task.id, { version: a.task.version, done: a.done }),
    onMutate: async a => {
      await qc.cancelQueries({ queryKey: taskKeys.mine(ws) });
      const prev = qc.getQueryData<{ tasks: TaskListItem[] }>(taskKeys.mine(ws));
      if (prev && a.done) qc.setQueryData(taskKeys.mine(ws), { ...prev, tasks: prev.tasks.filter(t => t.id !== a.task.id) });
      // t196u: reopening from Done leaves the Done list at once.
      const prevDone = qc.getQueryData<{ tasks: TaskListItem[] }>(taskKeys.mineDone(ws));
      if (prevDone && !a.done) qc.setQueryData(taskKeys.mineDone(ws), { ...prevDone, tasks: prevDone.tasks.filter(t => t.id !== a.task.id) });
      return { prev, prevDone };
    },
    onError: (_e, _a, ctx) => { if (ctx?.prev) qc.setQueryData(taskKeys.mine(ws), ctx.prev); if (ctx?.prevDone) qc.setQueryData(taskKeys.mineDone(ws), ctx.prevDone); },
    onSuccess: t => { qc.setQueryData(qk.task(t.id), t); patchTaskLists(qc, t as Partial<TaskListItem> & { id: string }); },
    onSettled: (_t, _e, a) => invalidateTasks(qc, ws, a.task.id),
  });
}
/** Quick capture: a task with no project (only the people it's for see it). */
export function useQuickCreateTask(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: TaskCreateFields) => dataApi().createStandaloneTask(ws!, b), onSettled: () => invalidateTasks(qc, ws) });
}

// ---- detail
export function useTask(id: string | null | undefined) {
  return useQuery({ queryKey: qk.task(id ?? ''), queryFn: () => dataApi().task(id!), enabled: Boolean(id) });
}
export function useTaskProject(projectId: string | null | undefined) {
  return useQuery({ queryKey: qk.project(projectId ?? ''), queryFn: () => dataApi().project(projectId!), enabled: Boolean(projectId), staleTime: 60_000 });
}
export function useTaskComments(id: string | null | undefined) {
  return useQuery({ queryKey: taskKeys.comments(id ?? ''), queryFn: () => dataApi().taskComments(id!), enabled: Boolean(id), select: d => d.comments });
}
/** Edit fields with the task's version; the server refuses a stale one (409) and the caller reloads. */
export function useUpdateTaskFields(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { task: Versioned; patch: Omit<UpdateTaskInput, 'version'> }) => dataApi().updateTask(a.task.id, { ...a.patch, version: a.task.version }),
    onSuccess: (t: TaskDetail) => { qc.setQueryData(qk.task(t.id), t); },
    onSettled: (_t, _e, a) => invalidateTasks(qc, ws, a.task.id),
  });
}
export function useAddTaskComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { taskId: string; body: string; mentions?: string[] }) => dataApi().addTaskComment(a.taskId, { body: a.body, mentions: a.mentions }),
    onSettled: (_c, _e, a) => Promise.all([qc.invalidateQueries({ queryKey: taskKeys.comments(a.taskId) }), qc.invalidateQueries({ queryKey: qk.task(a.taskId) })]),
  });
}
export function useAddSubtask(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { parent: Pick<TaskSummary, 'id' | 'project_id'>; title: string }) => dataApi().createTask(a.parent.project_id, { title: a.title.trim(), parent_task_id: a.parent.id }),
    onSettled: (_t, _e, a) => invalidateTasks(qc, ws, a.parent.id),
  });
}

/** t293u: write read / unread into every cached task list (Tasks area lists and project lists) without a refetch. */
export function patchTasksUnread(qc: QueryClient, ids: string[], unread: boolean) {
  const set = new Set(ids);
  for (const [key, data] of qc.getQueriesData<{ tasks: TaskListItem[] }>({ queryKey: ['tasks-area'] })) {
    if (!data?.tasks?.some(t => set.has(t.id))) continue;
    qc.setQueryData(key, { ...data, tasks: data.tasks.map(t => (set.has(t.id) ? { ...t, unread } : t)) });
  }
  for (const [key, data] of qc.getQueriesData<TaskSummary[]>({ queryKey: ['tasks'] })) {
    if (!Array.isArray(data) || !data.some(t => set.has(t.id))) continue;
    qc.setQueryData(key, data.map(t => (set.has(t.id) ? { ...t, unread } : t)));
  }
}

/** t293u: bulk Mark as read / unread from a task list. */
export function useSetTasksRead(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, read }: { ids: string[]; read: boolean }) => dataApi().setTasksRead(ws!, ids, read),
    onSuccess: (_r, v) => patchTasksUnread(qc, v.ids, !v.read),
  });
}

/** t293u: opening a task marks it read for you (on open and again on close, so your own edits while it's open don't count). */
export function useMarkTaskRead(ws: Ws, id: string | null | undefined, loaded: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!ws || !id || !loaded) return;
    const mark = () => Promise.resolve().then(() => dataApi().setTasksRead(ws, [id], true)).then(() => patchTasksUnread(qc, [id], false)).catch(() => undefined);
    void mark();
    return () => { void mark(); };
  }, [ws, id, loaded, qc]);
}

// ---- t333u: expand a task in a list to see its subtasks
/**
 * Subtask rows live under the Tasks-area key prefix ({ tasks } like the lists), so completing one is patched in place
 * by patchTaskLists and every task change refetches them with the lists.
 */
export const subtasksKey = (ws: Ws, parentId: string) => ['tasks-area', ws ?? '', { subtasks_of: parentId }] as const;
export const SUBTASK_BATCH = 100;

type Waiter = { res: (v: SubtaskItem[]) => void; rej: (e: unknown) => void };
const batches = new Map<string, Map<string, Waiter[]>>();
/**
 * Coalesces the subtask loads asked for in the same tick (one row, or "Expand all", or a refetch of every open row)
 * into one request per 100 parents, so a list never makes a request per task.
 */
export function loadSubtasks(ws: string, parentId: string, fetch: (ws: string, ids: string[]) => Promise<{ subtasks: SubtaskItem[] }> = (w, ids) => dataApi().subtasksOf(w, ids)): Promise<SubtaskItem[]> {
  return new Promise((res, rej) => {
    let b = batches.get(ws);
    if (!b) {
      b = new Map();
      batches.set(ws, b);
      setTimeout(() => {
        batches.delete(ws);
        const ids = [...b!.keys()];
        for (let i = 0; i < ids.length; i += SUBTASK_BATCH) {
          const chunk = ids.slice(i, i + SUBTASK_BATCH);
          fetch(ws, chunk).then(r => {
            const by = new Map<string, SubtaskItem[]>();
            for (const s of r.subtasks) { const k = s.parent_task_id ?? ''; by.set(k, [...(by.get(k) ?? []), s]); }
            for (const id of chunk) for (const w of b!.get(id) ?? []) w.res(by.get(id) ?? []);
          }, e => { for (const id of chunk) for (const w of b!.get(id) ?? []) w.rej(e); });
        }
      }, 0);
    }
    b.set(parentId, [...(b.get(parentId) ?? []), { res, rej }]);
  });
}

export function useSubtasks(ws: Ws, parentId: string, enabled = true) {
  return useQuery({ queryKey: subtasksKey(ws, parentId), queryFn: async () => ({ tasks: await loadSubtasks(ws!, parentId) }), enabled: Boolean(ws) && enabled, staleTime: 30_000, select: d => d.tasks });
}

/** Which tasks are expanded, for this session (sessionStorage on the web; memory in the app). */
const EXPANDED_KEY = 'collab.tasks.expanded';
const expanded = new Set<string>(readExpanded());
const listeners = new Set<() => void>();
let snapshot: ReadonlySet<string> = new Set(expanded);
function readExpanded(): string[] {
  try { const v = JSON.parse(globalThis.sessionStorage?.getItem(EXPANDED_KEY) ?? '[]'); return Array.isArray(v) ? v.filter(x => typeof x === 'string') : []; } catch { return []; }
}
function commit() {
  snapshot = new Set(expanded);
  try { globalThis.sessionStorage?.setItem(EXPANDED_KEY, JSON.stringify([...expanded].slice(-500))); } catch { /* private mode */ }
  listeners.forEach(l => l());
}
export function setTasksExpanded(ids: readonly string[], on: boolean) {
  for (const id of ids) { if (on) expanded.add(id); else expanded.delete(id); }
  commit();
}
export function useExpandedTasks() {
  const set = useSyncExternalStore(l => { listeners.add(l); return () => { listeners.delete(l); }; }, () => snapshot, () => snapshot);
  return { isOpen: (id: string) => set.has(id), toggle: (id: string) => setTasksExpanded([id], !set.has(id)), set: setTasksExpanded };
}
